/**
 * Tiny dependency-free chart primitive, line or bar. Honest about what
 * it doesn't do (no axis labels, no tooltips beyond aria-label) so the
 * agent doesn't reach for it when the user needs analytical depth.
 */

export type ChartPoint = { label: string; value: number };
export type ChartProps = {
  kind: "line" | "bar";
  points: ReadonlyArray<ChartPoint>;
  height?: number;
  caption?: string;
};

export function Chart({ kind, points, height = 140, caption }: ChartProps) {
  if (points.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-canvas-2 p-10 text-center text-ink-soft text-sm">
        No data for this range.
      </div>
    );
  }
  const max = Math.max(...points.map((p) => p.value), 1);
  const w = 480;
  const h = height;
  const padX = 24;
  const padY = 12;
  const ix = (i: number) =>
    padX + (i * (w - 2 * padX)) / Math.max(points.length - 1, 1);
  const iy = (v: number) =>
    h - padY - (v / max) * (h - 2 * padY);

  return (
    <figure className="rounded-xl border border-line bg-canvas-2 p-4">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-auto"
        role="img"
        aria-label={caption ?? `${kind} chart with ${points.length} points`}
      >
        {kind === "line" && (
          <polyline
            fill="none"
            stroke="oklch(0.62 0.16 195)"
            strokeWidth="2"
            points={points.map((p, i) => `${ix(i)},${iy(p.value)}`).join(" ")}
          />
        )}
        {kind === "bar" &&
          points.map((p, i) => {
            const bw = (w - 2 * padX) / points.length - 6;
            return (
              <rect
                key={i}
                x={ix(i) - bw / 2}
                y={iy(p.value)}
                width={bw}
                height={h - padY - iy(p.value)}
                fill="oklch(0.62 0.16 195)"
                rx="3"
              />
            );
          })}
      </svg>
      {caption && (
        <figcaption className="text-xs text-ink-soft mt-2 text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
